<script setup>
import {ref, computed} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import Icon from "../../../../../elements/Icon.vue";

const props = defineProps({
    taxForms: {
        type: Array,
        default: () => []
    },
    taxFormTypes: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['tax-form-uploaded', 'tax-form-deleted']);

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const selectedFormType = ref('w9');
const uploading = ref(false);
const deleting = ref(null);
const uploadError = ref(null);
const fileInput = ref(null);
const isDragging = ref(false);
const formToDelete = ref(null);
const deleteModal = ref(null);

const uploadUrl = computed(() => route('partners_int.tax-forms.upload'));
const deleteUrl = computed(() => route('partners_int.tax-forms.delete'));

const formTypeOptions = computed(() => {
    return Object.entries(props.taxFormTypes).map(([value, label]) => ({
        value,
        label
    }));
});

const getFormTypeDescription = (formType) => {
    const descriptions = {
        'w9': portalContentAndDesignStore.translationSettings.portal_translation_profile_w9_description || 'For U.S. citizens and resident aliens. Required for tax reporting purposes.',
        'w8ben': portalContentAndDesignStore.translationSettings.portal_translation_profile_w8ben_description || 'For non-U.S. individuals. Used to claim tax treaty benefits and certify foreign status.',
        'w8ben_e': portalContentAndDesignStore.translationSettings.portal_translation_profile_w8bene_description || 'For non-U.S. entities (companies, partnerships, etc.). Used to certify foreign status and claim treaty benefits.'
    };
    return descriptions[formType] || '';
};

const getExistingFormForType = (formType) => {
    return props.taxForms.find(f => f.form_type === formType);
};

const currentForm = computed(() => {
    return getExistingFormForType(selectedFormType.value);
});

const getStatusBadgeClass = (status) => {
    switch (status) {
        case 'rejected':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-green-100 text-green-800';
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'rejected':
            return portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_form_rejected || 'Rejected';
        default:
            return portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_form_uploaded || 'Uploaded';
    }
};

const getFormTypeLabel = (form) => {
    return form?.form_type_label || form?.form_type || '';
};

const triggerFileInput = () => {
    fileInput.value?.click();
};

const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await uploadFile(file);
};

const handleDrop = async (event) => {
    isDragging.value = false;
    const file = event.dataTransfer.files[0];
    if (!file) return;
    await uploadFile(file);
};

const uploadFile = async (file) => {
    uploadError.value = null;
    uploading.value = true;

    const formData = new FormData();
    formData.append('form_type', selectedFormType.value);
    formData.append('tax_form', file);

    try {
        const response = await axios.post(uploadUrl.value, formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        });

        if (response.data.status === 1) {
            emit('tax-form-uploaded', response.data.tax_form);
        } else {
            uploadError.value = response.data.error_message || 'Upload failed. Please try again.';
        }
    } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors) {
            const errors = error.response.data.errors;
            uploadError.value = Object.values(errors).flat().join(' ');
        } else {
            uploadError.value = error.response?.data?.error_message || 'Upload failed. Please try again.';
        }
    } finally {
        uploading.value = false;
        // Reset file input
        if (fileInput.value) {
            fileInput.value.value = '';
        }
    }
};

const openDeleteModal = (taxForm) => {
    formToDelete.value = taxForm;
    if (deleteModal.value) {
        deleteModal.value.showModal = true;
    }
};

const confirmDeleteTaxForm = async () => {
    if (!formToDelete.value) return;

    const taxForm = formToDelete.value;
    deleting.value = taxForm.id;

    try {
        const response = await axios.post(deleteUrl.value, {
            tax_form_id: taxForm.id
        });

        if (response.data.status === 1) {
            emit('tax-form-deleted', taxForm.id);
            if (deleteModal.value) {
                deleteModal.value.showModal = false;
            }
            formToDelete.value = null;
        } else {
            uploadError.value = response.data.error_message || 'Delete failed. Please try again.';
        }
    } catch (error) {
        uploadError.value = error.response?.data?.error_message || 'Delete failed. Please try again.';
    } finally {
        deleting.value = null;
    }
};

</script>

<template>
    <div class="space-y-6">
        <!-- Form Type Selector with Descriptions -->
        <div class="space-y-3">
            <label class="block text-gray-900 text-sm mb-1.5">
                {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_form_type || 'Form' }}
            </label>

            <div class="space-y-2">
                <label
                    v-for="option in formTypeOptions"
                    :key="option.value"
                    :class="[
                        'relative flex cursor-pointer rounded border p-3 transition-all',
                        selectedFormType === option.value
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                    ]"
                >
                    <input
                        type="radio"
                        :value="option.value"
                        v-model="selectedFormType"
                        class="sr-only"
                    />
                    <div class="flex w-full">
                        <div class="flex-shrink-0 pt-0.5">
                            <span :class="[
                                'flex h-4 w-4 items-center justify-center rounded-full border',
                                selectedFormType === option.value
                                    ? 'border-gray-900 bg-gray-900'
                                    : 'border-gray-300 bg-white'
                            ]">
                                <span v-if="selectedFormType === option.value" class="h-1.5 w-1.5 rounded-full bg-white"></span>
                            </span>
                        </div>
                        <div class="ml-3 flex-1">
                            <span :class="[
                                'block text-sm font-medium',
                                selectedFormType === option.value ? 'text-gray-900' : 'text-gray-700'
                            ]">
                                {{ option.label }}
                            </span>
                            <span class="mt-0.5 block text-xs text-gray-500">
                                {{ getFormTypeDescription(option.value) }}
                            </span>
                        </div>
                    </div>
                </label>
            </div>
        </div>

        <!-- Error Message -->
        <div v-if="uploadError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm flex items-start">
            <svg class="h-5 w-5 text-red-400 flex-shrink-0 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path fill="none" d="M0 0h24v24H0z"/>
                <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z"/>
            </svg>
            {{ uploadError }}
        </div>

        <!-- Current Form Display -->
        <div v-if="currentForm" class="border border-gray-200 rounded p-4 bg-gray-50">
            <div class="flex items-start justify-between">
                <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                        <svg class="w-10 h-10 text-gray-400" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M9 2.003V2h10.998C20.55 2 21 2.455 21 2.992v18.016a.993.993 0 0 1-.993.992H3.993A1 1 0 0 1 3 20.993V8l6-5.997zM5.83 8H9V4.83L5.83 8zM11 4v5a1 1 0 0 1-1 1H5v10h14V4h-8z"/>
                        </svg>
                    </div>
                    <div>
                        <div class="font-medium text-gray-900">{{ currentForm.form_type_label }}</div>
                        <div class="text-sm text-gray-500">{{ currentForm.original_filename }}</div>
                        <div class="text-xs text-gray-400 mt-1">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_uploaded_on || 'Uploaded on' }}
                            {{ currentForm.created_at_formatted }}
                        </div>
                    </div>
                </div>
                <div class="flex items-center space-x-3">
                    <span :class="['px-2.5 py-1 text-xs font-medium rounded-full', getStatusBadgeClass(currentForm.status)]">
                        {{ getStatusLabel(currentForm.status) }}
                    </span>
                    <a
                        :href="currentForm.url"
                        target="_blank"
                        class="text-gray-400 hover:text-gray-600 transition-colors"
                        :title="portalContentAndDesignStore.translationSettings.portal_translation_profile_view || 'View'"
                    >
                        <svg class="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.819-9C2.121 6.88 6.608 3 12 3zm0 16a9.005 9.005 0 0 0 8.777-7 9.005 9.005 0 0 0-17.554 0A9.005 9.005 0 0 0 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
                        </svg>
                    </a>
                    <button
                        type="button"
                        @click="openDeleteModal(currentForm)"
                        :disabled="deleting === currentForm.id"
                        class="text-gray-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                        :title="portalContentAndDesignStore.translationSettings.portal_translation_profile_delete || 'Delete'"
                    >
                        <svg v-if="deleting !== currentForm.id" class="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"/>
                        </svg>
                        <svg v-else class="w-5 h-5 animate-spin" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M3.055 13H5.07a7.002 7.002 0 0 0 13.858 0h2.016a9.001 9.001 0 0 1-17.89 0zm0-2a9.001 9.001 0 0 1 17.89 0H18.93a7.002 7.002 0 0 0-13.858 0H3.055z"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div v-if="currentForm.notes && currentForm.status === 'rejected'" class="mt-3 text-sm text-red-600 bg-red-50 rounded p-3">
                <strong>{{ portalContentAndDesignStore.translationSettings.portal_translation_profile_rejection_reason || 'Rejection reason' }}:</strong> {{ currentForm.notes }}
            </div>
        </div>

        <!-- Upload Area -->
        <div class="space-y-3">
            <input
                ref="fileInput"
                type="file"
                accept=".pdf,image/*"
                class="hidden"
                @change="handleFileSelect"
            />

            <div
                @click="triggerFileInput"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                :class="[
                    'relative border border-dashed rounded p-6 text-center cursor-pointer transition-all',
                    isDragging ? 'border-gray-400 bg-gray-50' : 'border-gray-300 hover:border-gray-400',
                    uploading ? 'pointer-events-none opacity-60' : ''
                ]"
            >
                <div v-if="!uploading">
                    <svg class="mx-auto h-10 w-10 text-gray-400" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path fill="none" d="M0 0h24v24H0z"/>
                        <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z"/>
                    </svg>
                    <p class="mt-2 text-sm text-gray-600">
                        <span class="font-medium text-gray-900">
                            {{ currentForm
                                ? (portalContentAndDesignStore.translationSettings.portal_translation_profile_click_to_replace || 'Click to replace')
                                : (portalContentAndDesignStore.translationSettings.portal_translation_profile_click_to_upload || 'Click to upload')
                            }}
                        </span>
                        {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_or_drag || 'or drag and drop' }}
                    </p>
                    <p class="mt-1 text-xs text-gray-500">
                        {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_accepted_formats || 'PDF, PNG, or JPG (max 10MB)' }}
                    </p>
                </div>
                <div v-else class="py-2">
                    <svg class="mx-auto h-8 w-8 text-gray-400 animate-spin" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path fill="none" d="M0 0h24v24H0z"/>
                        <path d="M3.055 13H5.07a7.002 7.002 0 0 0 13.858 0h2.016a9.001 9.001 0 0 1-17.89 0zm0-2a9.001 9.001 0 0 1 17.89 0H18.93a7.002 7.002 0 0 0-13.858 0H3.055z"/>
                    </svg>
                    <p class="mt-2 text-sm text-gray-600">
                        {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_uploading || 'Uploading...' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- All Uploaded Forms List -->
        <div v-if="taxForms.length > 1" class="mt-6">
            <h4 class="text-sm font-medium text-gray-700 mb-3">
                {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_all_tax_forms || 'All Uploaded Tax Forms' }}
            </h4>
            <div class="space-y-2">
                <div
                    v-for="form in taxForms"
                    :key="form.id"
                    class="flex items-center justify-between py-3 px-4 bg-white border border-gray-200 rounded text-sm"
                >
                    <div class="flex items-center space-x-3">
                        <svg class="w-6 h-6 text-gray-400" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M9 2.003V2h10.998C20.55 2 21 2.455 21 2.992v18.016a.993.993 0 0 1-.993.992H3.993A1 1 0 0 1 3 20.993V8l6-5.997zM5.83 8H9V4.83L5.83 8zM11 4v5a1 1 0 0 1-1 1H5v10h14V4h-8z"/>
                        </svg>
                        <div>
                            <span class="font-medium text-gray-900">{{ form.form_type_label }}</span>
                            <span class="text-gray-400 mx-2">•</span>
                            <span class="text-gray-500">{{ form.original_filename }}</span>
                        </div>
                    </div>
                    <div class="flex items-center space-x-3">
                        <span :class="['px-2.5 py-1 text-xs font-medium rounded-full', getStatusBadgeClass(form.status)]">
                            {{ getStatusLabel(form.status) }}
                        </span>
                        <a
                            :href="form.url"
                            target="_blank"
                            class="text-gray-400 hover:text-gray-600 transition-colors"
                            :title="portalContentAndDesignStore.translationSettings.portal_translation_profile_view || 'View'"
                        >
                            <svg class="w-4 h-4" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path fill="none" d="M0 0h24v24H0z"/>
                                <path d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.819-9C2.121 6.88 6.608 3 12 3zm0 16a9.005 9.005 0 0 0 8.777-7 9.005 9.005 0 0 0-17.554 0A9.005 9.005 0 0 0 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <modal
            title="Delete tax form"
            size="small"
            name="delete_tax_form_modal"
            ref="deleteModal"
        >
            <template #body>
                <div class="space-y-4">
                    <p class="text-sm text-gray-600">
                        Are you sure you want to delete this tax form?
                    </p>
                    <div v-if="formToDelete" class="p-3 bg-gray-50 rounded text-sm">
                        <p class="font-medium text-gray-900">{{ getFormTypeLabel(formToDelete) }}</p>
                        <p class="text-gray-500">{{ formToDelete.original_filename }}</p>
                    </div>
                    <p class="text-sm text-gray-500">
                        This action cannot be undone.
                    </p>
                </div>
            </template>
            <template #footer>
                <btn
                    @click="confirmDeleteTaxForm"
                    type="danger"
                    :loading="deleting !== null"
                    :disabled="deleting !== null"
                >
                    Delete
                </btn>
            </template>
        </modal>
    </div>
</template>
